import { onAuthStateChanged, signOut, type User } from "firebase/auth";
import * as Sentry from "@sentry/react-native";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type PropsWithChildren } from "react";

import {
  accessBootstrapFailureSignal,
  accessBootstrapMatchesUser,
  accessBootstrapRetryDelayMs
} from "@/auth/accessBootstrap";
import { firebaseSessionRegistry } from "@/auth/authSession";
import { getClientDeviceIdentity, type ClientDeviceIdentity } from "@/devices/deviceIdentity";
import { activateDevice, claimAccess } from "@/firebase/callables";
import { firebaseAuth, hasFirebaseClientConfig } from "@/firebase/client";
import { useDemoMode } from "@/demo/DemoModeProvider";
import { clearDecodedImages } from "@/lib/imageReadiness";
import { clearPersistentArtwork } from "@/lib/persistentArtwork";
import { useReadinessHold, useResetReadiness } from "@/providers/ReadinessGate";

type AuthState = {
  loading: boolean;
  user: User | null;
  /** True when running without Firebase config (local design/dev mode). */
  devMode: boolean;
  /** Public synthetic review session, isolated from Firebase account state. */
  demoMode: boolean;
  /** Treat as authenticated: real user, or dev mode without Firebase. */
  signedIn: boolean;
  isAdmin: boolean;
  device: ClientDeviceIdentity | null;
  /** Device activation has settled for this document; playback may evaluate eligibility. */
  deviceActivationComplete: boolean;
  deviceLimitReached: boolean;
  swapsRemaining: number | null;
  nextSwapAvailableAt: string | null;
  ensureActiveDevice: (options?: { throwOnError?: boolean }) => Promise<boolean>;
  authBootstrapComplete: boolean;
  /** Stable for one Firebase login; changes after a real sign-out/sign-in. */
  authSessionKey: string | null;
  signOutUser: () => Promise<void>;
};

const AuthContext = createContext<AuthState>({
  loading: true,
  user: null,
  devMode: false,
  demoMode: false,
  signedIn: false,
  isAdmin: false,
  device: null,
  deviceActivationComplete: false,
  deviceLimitReached: false,
  swapsRemaining: null,
  nextSwapAvailableAt: null,
  ensureActiveDevice: async () => false,
  authBootstrapComplete: false,
  authSessionKey: null,
  signOutUser: async () => {}
});

const bootstrappedUids = new Set<string>();
const deviceBootstrappedUids = new Set<string>();
const adminClaimsByUid = new Map<string, boolean>();

export function AuthProvider({ children }: PropsWithChildren) {
  const {
    active: demoMode,
    loading: demoLoading,
    exitDemo
  } = useDemoMode();
  // Firebase retains currentUser outside React. Reuse it when Expo restores
  // browser history instead of briefly pretending auth is starting cold.
  const [state, setState] = useState<{
    loading: boolean;
    user: User | null;
    authSessionKey: string | null;
  }>(() => {
    const user = firebaseAuth?.currentUser ?? null;
    return {
      loading: firebaseAuth != null && user == null,
      user,
      authSessionKey: user
        ? firebaseSessionRegistry.begin({
            uid: user.uid,
            lastSignInTime: user.metadata.lastSignInTime
          })
        : null
    };
  });
  const [device, setDevice] = useState<ClientDeviceIdentity | null>(null);
  const [deviceActivationComplete, setDeviceActivationComplete] = useState(() => {
    const user = firebaseAuth?.currentUser ?? null;
    return !hasFirebaseClientConfig || (user != null && deviceBootstrappedUids.has(user.uid));
  });
  const [deviceLimitReached, setDeviceLimitReached] = useState(false);
  const [swapsRemaining, setSwapsRemaining] = useState<number | null>(null);
  const [nextSwapAvailableAt, setNextSwapAvailableAt] = useState<string | null>(null);
  const [accessBootstrappedUid, setAccessBootstrappedUid] = useState<string | null>(() => {
    const user = firebaseAuth?.currentUser ?? null;
    return user != null && bootstrappedUids.has(user.uid) ? user.uid : null;
  });
  const [isAdmin, setIsAdmin] = useState(() => {
    const user = firebaseAuth?.currentUser ?? null;
    return user ? (adminClaimsByUid.get(user.uid) ?? false) : false;
  });
  const [adminClaimResolved, setAdminClaimResolved] = useState(() => {
    const user = firebaseAuth?.currentUser ?? null;
    return !hasFirebaseClientConfig || user == null || adminClaimsByUid.has(user.uid);
  });
  const previousUidRef = useRef<string | null | undefined>(undefined);
  const resetReadiness = useResetReadiness();
  const authBootstrapComplete = !hasFirebaseClientConfig || accessBootstrapMatchesUser(
    accessBootstrappedUid,
    state.user?.uid ?? null
  );

  const ensureActiveDevice = useCallback(async (
    options?: { throwOnError?: boolean }
  ): Promise<boolean> => {
    if (!hasFirebaseClientConfig || demoMode) return true;
    if (!firebaseAuth?.currentUser) return false;

    const uid = firebaseAuth.currentUser.uid;
    setDeviceActivationComplete(false);
    try {
      const identity = device ?? (await getClientDeviceIdentity());
      const activation = await activateDevice(identity);
      const active = activation.status !== "device-limit-reached";
      setDevice(identity);
      setDeviceLimitReached(!active);
      setSwapsRemaining(
        typeof activation.swapsRemaining === "number" ? activation.swapsRemaining : null
      );
      setNextSwapAvailableAt(activation.nextSwapAvailableAt ?? null);
      return active;
    } catch (error) {
      const details =
        typeof error === "object" && error != null && "details" in error
          ? (error as { details?: unknown }).details
          : null;
      if (typeof details === "object" && details != null) {
        const quota = details as {
          swapsRemaining?: unknown;
          nextSwapAvailableAt?: unknown;
        };
        if (typeof quota.swapsRemaining === "number") {
          setSwapsRemaining(quota.swapsRemaining);
        }
        if (typeof quota.nextSwapAvailableAt === "string") {
          setNextSwapAvailableAt(quota.nextSwapAvailableAt);
        }
      }
      if (options?.throwOnError) throw error;
      return false;
    } finally {
      deviceBootstrappedUids.add(uid);
      setDeviceActivationComplete(true);
    }
  }, [demoMode, device]);

  useEffect(() => {
    if (!firebaseAuth) {
      setState({ loading: false, user: null, authSessionKey: null });
      return undefined;
    }

    return onAuthStateChanged(firebaseAuth, (user) => {
      const previousUid = previousUidRef.current;
      const nextUid = user?.uid ?? null;
      if (previousUid !== undefined && nextUid !== previousUid) {
        clearDecodedImages();
        if (previousUid) {
          firebaseSessionRegistry.end(previousUid);
          void clearPersistentArtwork(previousUid);
          bootstrappedUids.delete(previousUid);
          deviceBootstrappedUids.delete(previousUid);
          adminClaimsByUid.delete(previousUid);
        }
        if (user) resetReadiness();
      }
      setAccessBootstrappedUid(
        nextUid && bootstrappedUids.has(nextUid) ? nextUid : null
      );
      previousUidRef.current = nextUid;
      setIsAdmin(nextUid ? (adminClaimsByUid.get(nextUid) ?? false) : false);
      setAdminClaimResolved(
        !hasFirebaseClientConfig || nextUid == null || adminClaimsByUid.has(nextUid)
      );
      setState({
        loading: false,
        user,
        authSessionKey: user
          ? firebaseSessionRegistry.begin({
              uid: user.uid,
              lastSignInTime: user.metadata.lastSignInTime
            })
          : null
      });
    });
  }, [resetReadiness]);

  useEffect(() => {
    if (state.user && demoMode) void exitDemo();
  }, [demoMode, exitDemo, state.user]);

  useEffect(() => {
    // Account deletion cannot reliably purge historical third-party event
    // payloads, so never attach the Firebase account identifier to Sentry.
    Sentry.setUser(null);
  }, [state.user]);

  useEffect(() => {
    if (demoLoading) return;
    if (demoMode) {
      setIsAdmin(false);
      setAdminClaimResolved(true);
      setDeviceActivationComplete(true);
      setDeviceLimitReached(false);
      setSwapsRemaining(null);
      setNextSwapAvailableAt(null);
      return;
    }
    if (!hasFirebaseClientConfig) {
      setIsAdmin(true);
      setAdminClaimResolved(true);
      setDeviceActivationComplete(true);
      return;
    }
    if (!state.user) {
      setIsAdmin(false);
      setAdminClaimResolved(true);
      setDeviceActivationComplete(false);
      setDeviceLimitReached(false);
      setSwapsRemaining(null);
      setNextSwapAvailableAt(null);
      setAccessBootstrappedUid(null);
      return;
    }
    if (bootstrappedUids.has(state.user.uid)) {
      setIsAdmin(adminClaimsByUid.get(state.user.uid) ?? false);
      setAdminClaimResolved(true);
      setAccessBootstrappedUid(state.user.uid);
      if (!deviceBootstrappedUids.has(state.user.uid)) {
        void ensureActiveDevice();
      }
      return;
    }

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let failedAttempts = 0;
    const uid = state.user.uid;
    const bootstrap = async () => {
      const [tokenResult, accessResult] = await Promise.allSettled([
        state.user!.getIdTokenResult(),
        claimAccess()
      ]);
      if (cancelled || firebaseAuth?.currentUser?.uid !== uid) return;

      if (tokenResult.status === "fulfilled") {
        const admin = tokenResult.value.claims.admin === true;
        adminClaimsByUid.set(uid, admin);
        setIsAdmin(admin);
      }
      setAdminClaimResolved(true);

      if (accessResult.status === "rejected") {
        const failureSignal = accessBootstrapFailureSignal(failedAttempts);
        if (failureSignal === "breadcrumb") {
          Sentry.addBreadcrumb({
            category: "auth.bootstrap",
            level: "warning",
            message: "claimAccess failed; retrying",
            data: { failedAttempt: failedAttempts + 1 }
          });
        } else if (failureSignal === "issue") {
          Sentry.captureException(accessResult.reason, {
            tags: { bootstrap: "claim-access" },
            extra: { failedAttempt: failedAttempts + 1 }
          });
        }
        retryTimer = setTimeout(() => {
          failedAttempts += 1;
          void bootstrap();
        }, accessBootstrapRetryDelayMs(failedAttempts));
        return;
      }

      // Access readiness belongs to this UID, not to the provider instance.
      // Account switches can otherwise inherit `true` from the prior user and
      // let an empty pre-claim catalog become the curtain's reveal frame.
      bootstrappedUids.add(uid);
      setAccessBootstrappedUid(uid);
      // Device registration controls playback eligibility, not whether an
      // authenticated family may enter its library. Keep it warm in the
      // background; createPlaybackSession still enforces the device limit if
      // someone presses Play before this request settles.
      void ensureActiveDevice();
    };
    void bootstrap();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [demoLoading, demoMode, ensureActiveDevice, state.user]);

  const loading = state.loading || demoLoading || !adminClaimResolved;
  const authReady = !loading && (demoMode || !state.user || authBootstrapComplete);
  useReadinessHold("auth", authReady);

  const value = useMemo<AuthState>(() => {
    const devMode = !hasFirebaseClientConfig;
    return {
      loading,
      user: state.user,
      devMode,
      demoMode,
      signedIn: devMode || demoMode || state.user != null,
      isAdmin: devMode && !demoMode ? true : isAdmin,
      device,
      deviceActivationComplete,
      deviceLimitReached,
      swapsRemaining,
      nextSwapAvailableAt,
      ensureActiveDevice,
      authBootstrapComplete,
      authSessionKey: state.authSessionKey,
      signOutUser: async () => {
        if (demoMode) {
          await exitDemo();
          return;
        }
        if (firebaseAuth) {
          const uid = firebaseAuth.currentUser?.uid;
          await signOut(firebaseAuth);
          firebaseSessionRegistry.end(uid);
        }
      }
    };
  }, [
    authBootstrapComplete,
    device,
    deviceActivationComplete,
    deviceLimitReached,
    demoMode,
    ensureActiveDevice,
    exitDemo,
    nextSwapAvailableAt,
    state,
    isAdmin,
    swapsRemaining,
    loading
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthState() {
  return useContext(AuthContext);
}
